import React, { useState, useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Button,
  Chip,
  Snackbar,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccountBalance,
  CreditCard as CreditCardIcon,
  CloudDownload as ScrapeIcon,
  Flag as FlagIcon,
  Mail as MailIcon,
  Language as LanguageIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  OpenInNew as OpenInNewIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService, AdminStats, Bank, AdminCard, ScrapeResult, TTBankWithCards, ScrapingLog } from '../services/admin';
import { AuthContext } from '../App';
import {
  gmailExpensesAdminService,
  AdminExpenseCategoryConfig,
  AdminMerchantPattern,
  AdminEmailIngestionLog,
  AdminClassificationReview,
} from '../services/gmailExpensesAdmin';

// Only these 5 traditional banks are allowed for scraping
const T_T_BANKS = [
  { code: 'REPUBLIC', name: 'Republic Bank Limited' },
  { code: 'RBC', name: 'RBC Royal Bank (T&T)' },
  { code: 'FIRST_CITIZENS', name: 'First Citizens Bank' },
  { code: 'SCOTIA', name: 'Scotiabank Trinidad and Tobago' },
  { code: 'JMMB', name: 'JMMB Bank (T&T)' },
];

const operatingCategoryLabel = (cat?: TTBankWithCards['operating_category']): string => {
  if (cat === 'TRADITIONAL') return 'Traditional bank';
  if (cat === 'NON_TRADITIONAL') return 'Non-traditional bank';
  if (cat === 'UNKNOWN') return 'Unknown category';
  return '';
};

const AdminPanel: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = parseInt(searchParams.get('tab') ?? '0', 10);
  const activeTab = tabFromUrl >= 0 && tabFromUrl <= 5 ? tabFromUrl : 0;
  useEffect(() => {
    if (searchParams.get('tab') === null || searchParams.get('tab') === '') {
      setSearchParams({ tab: '0' }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [scrapingStatus, setScrapingStatus] = useState<{ [key: string]: 'idle' | 'scraping' | 'success' | 'error' }>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const auth = useContext(AuthContext);
  const queryClient = useQueryClient();

  const { data: stats, isLoading: statsLoading } = useQuery<AdminStats>({
    queryKey: ['adminStats'],
    queryFn: adminService.getDashboardStats,
    enabled: activeTab === 0 && !!auth?.user?.is_staff,
  });

  const { data: banks, isLoading: banksLoading } = useQuery<Bank[]>({
    queryKey: ['adminBanks'],
    queryFn: adminService.getBanks,
    enabled: activeTab === 1 && !!auth?.user?.is_staff,
  });

  const { data: cards, isLoading: cardsLoading } = useQuery<AdminCard[]>({
    queryKey: ['adminCards'],
    queryFn: adminService.getCards,
    enabled: activeTab === 2 && !!auth?.user?.is_staff,
  });

  // Gmail Expenses admin data (tab index 5)
  const { data: expenseCategories = [], isLoading: categoriesLoading } = useQuery<AdminExpenseCategoryConfig[]>({
    queryKey: ['gmailExpenseAdminCategories'],
    queryFn: gmailExpensesAdminService.listCategories,
    enabled: activeTab === 5 && !!auth?.user?.is_staff,
  });

  const { data: merchantPatterns = [], isLoading: patternsLoading } = useQuery<AdminMerchantPattern[]>({
    queryKey: ['gmailExpenseAdminMerchantPatterns'],
    queryFn: gmailExpensesAdminService.listMerchantPatterns,
    enabled: activeTab === 5 && !!auth?.user?.is_staff,
  });

  const { data: ingestionLogs = [], isLoading: logsLoading } = useQuery<AdminEmailIngestionLog[]>({
    queryKey: ['gmailExpenseAdminIngestionLogs'],
    queryFn: gmailExpensesAdminService.listIngestionLogs,
    enabled: activeTab === 5 && !!auth?.user?.is_staff,
  });

  const { data: classificationReviews = [], isLoading: reviewsLoading } = useQuery<AdminClassificationReview[]>({
    queryKey: ['gmailExpenseAdminClassificationReviews'],
    queryFn: gmailExpensesAdminService.listClassificationReviews,
    enabled: activeTab === 5 && !!auth?.user?.is_staff,
  });

  // Scraping logs query (tab index 4)
  const { data: scrapingLogsData, isLoading: scrapingLogsLoading } = useQuery<{ logs: ScrapingLog[] }>({
    queryKey: ['scrapingLogs'],
    queryFn: adminService.getScrapingLogs,
    enabled: activeTab === 4 && !!auth?.user?.is_staff,
  });

  const [selectedLogId, setSelectedLogId] = useState<number | null>(null);

  const seedCategoriesMutation = useMutation({
    mutationFn: gmailExpensesAdminService.seedDefaultCategories,
    onSuccess: (data) => {
      setSnackbar({ open: true, message: `Seeded categories: ${data.created}`, severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['gmailExpenseAdminCategories'] });
    },
    onError: (err: any) => setSnackbar({ open: true, message: err.message || 'Failed to seed categories', severity: 'error' }),
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<AdminExpenseCategoryConfig> }) =>
      gmailExpensesAdminService.updateCategory(id, data),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Category updated', severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['gmailExpenseAdminCategories'] });
    },
    onError: (err: any) => setSnackbar({ open: true, message: err.message || 'Failed to update category', severity: 'error' }),
  });

  const createPatternMutation = useMutation({
    mutationFn: gmailExpensesAdminService.createMerchantPattern,
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Merchant pattern saved', severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['gmailExpenseAdminMerchantPatterns'] });
    },
    onError: (err: any) => setSnackbar({ open: true, message: err.message || 'Failed to save pattern', severity: 'error' }),
  });

  const updatePatternMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<AdminMerchantPattern> }) =>
      gmailExpensesAdminService.updateMerchantPattern(id, data),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Merchant pattern updated', severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['gmailExpenseAdminMerchantPatterns'] });
    },
    onError: (err: any) => setSnackbar({ open: true, message: err.message || 'Failed to update pattern', severity: 'error' }),
  });

  const deletePatternMutation = useMutation({
    mutationFn: gmailExpensesAdminService.deleteMerchantPattern,
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Merchant pattern deleted', severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['gmailExpenseAdminMerchantPatterns'] });
    },
    onError: (err: any) => setSnackbar({ open: true, message: err.message || 'Failed to delete pattern', severity: 'error' }),
  });

  const createReviewMutation = useMutation({
    mutationFn: gmailExpensesAdminService.createClassificationReview,
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Review saved', severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['gmailExpenseAdminClassificationReviews'] });
    },
    onError: (err: any) => setSnackbar({ open: true, message: err.message || 'Failed to save review', severity: 'error' }),
  });

  // Dialog state for merchant patterns
  const [patternDialogOpen, setPatternDialogOpen] = useState(false);
  const [editingPattern, setEditingPattern] = useState<AdminMerchantPattern | null>(null);
  const [patternForm, setPatternForm] = useState<Partial<AdminMerchantPattern>>({
    name: '',
    is_active: true,
    priority: 100,
    match_type: 'CONTAINS',
    match_value: '',
    merchant_canonical: '',
    category: 'OTHER',
  });
  const [reviewTxnId, setReviewTxnId] = useState<number | ''>('');

  const openNewPatternDialog = () => {
    setEditingPattern(null);
    setPatternForm({
      name: '',
      is_active: true,
      priority: 100,
      match_type: 'CONTAINS',
      match_value: '',
      merchant_canonical: '',
      category: 'OTHER',
    });
    setPatternDialogOpen(true);
  };

  const openEditPatternDialog = (p: AdminMerchantPattern) => {
    setEditingPattern(p);
    setPatternForm({
      name: p.name,
      is_active: p.is_active,
      priority: p.priority,
      match_type: p.match_type,
      match_value: p.match_value,
      merchant_canonical: p.merchant_canonical || '',
      category: p.category,
    });
    setPatternDialogOpen(true);
  };

  const savePattern = () => {
    if (!patternForm.name || !patternForm.match_value) {
      setSnackbar({ open: true, message: 'Name and match value are required', severity: 'error' });
      return;
    }
    if (editingPattern) {
      updatePatternMutation.mutate({ id: editingPattern.id, data: patternForm });
    } else {
      createPatternMutation.mutate(patternForm);
    }
    setPatternDialogOpen(false);
  };


  const scrapeMutation = useMutation({
    mutationFn: (bankCode: string) => adminService.scrapeTrinidadTobagoBank(bankCode),
    onSuccess: (result: ScrapeResult, variables: string) => {
      const bankCode = variables;
      setScrapingStatus((prev) => ({ ...prev, [bankCode]: 'success' }));
      queryClient.invalidateQueries({ queryKey: ['adminCards'] });
      queryClient.invalidateQueries({ queryKey: ['adminBanks'] });
      queryClient.invalidateQueries({ queryKey: ['adminStats'] });
      queryClient.invalidateQueries({ queryKey: ['scrapingLogs'] }); // Refresh scraping logs
      if (result.status === 'success') {
        setSnackbar({
          open: true,
          message: `${T_T_BANKS.find(b => b.code === bankCode)?.name}: Created ${result.created || 0} cards, Updated ${result.updated || 0} cards`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: result.error || 'Scraping failed',
          severity: 'error',
        });
      }
      setTimeout(() => {
        setScrapingStatus((prev) => ({ ...prev, [bankCode]: 'idle' }));
      }, 3000);
    },
    onError: (error: any, variables: string) => {
      const bankCode = variables;
      setScrapingStatus((prev) => ({ ...prev, [bankCode]: 'error' }));
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.response?.data?.detail || 'Scraping failed',
        severity: 'error',
      });
      setTimeout(() => {
        setScrapingStatus((prev) => ({ ...prev, [bankCode]: 'idle' }));
      }, 3000);
    },
  });

  const handleScrape = (bankCode: string) => {
    setScrapingStatus((prev) => ({ ...prev, [bankCode]: 'scraping' }));
    scrapeMutation.mutate(bankCode);
  };

  const handleScrapeAll = () => {
    // Scrape all banks - they will run in parallel but each has its own status
    T_T_BANKS.forEach((bank) => {
      if (scrapingStatus[bank.code] !== 'scraping') {
        handleScrape(bank.code);
      }
    });
  };

  if (!auth?.user?.is_staff) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Access Denied: You do not have administrative privileges.</Alert>
      </Container>
    );
  }

  const pageTitles: Record<number, string> = {
    0: 'Dashboard',
    1: 'Banks',
    2: 'Cards',
    3: 'T&T Banks',
    4: 'Scraping',
    5: 'Gmail Expenses',
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', bgcolor: '#f8fafc' }}>
      <Container maxWidth="xl" disableGutters>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {pageTitles[activeTab]}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 0 && 'Overview of users, banks, cards and offers'}
            {activeTab === 1 && 'Manage banks'}
            {activeTab === 2 && 'Manage credit cards'}
            {activeTab === 3 && 'Trinidad & Tobago banks and cards'}
            {activeTab === 4 && 'Scrape bank websites for cards'}
            {activeTab === 5 && 'Gmail expense categories and rules'}
            </Typography>
        </Box>

      {activeTab === 0 && (
        <Grid container spacing={3}>
          {statsLoading ? (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                <CircularProgress size={48} />
              </Box>
            </Grid>
          ) : stats ? (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: 4,
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <AccountBalance sx={{ fontSize: 32, opacity: 0.9 }} />
                      <Box>
                        <Typography variant="h4" fontWeight={800}>
                          {stats.total_users}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total Users
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, #f093fb 0%, #4facfe 100%)',
                    color: 'white',
                    borderRadius: 4,
                    boxShadow: '0 8px 24px rgba(240, 147, 251, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 32px rgba(240, 147, 251, 0.4)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <AccountBalance sx={{ fontSize: 32, opacity: 0.9 }} />
                      <Box>
                        <Typography variant="h4" fontWeight={800}>
                          {stats.total_banks}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total Banks
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                    color: 'white',
                    borderRadius: 4,
                    boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 32px rgba(16, 185, 129, 0.4)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <CreditCardIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                      <Box>
                        <Typography variant="h4" fontWeight={800}>
                          {stats.total_cards}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total Cards
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </>
          ) : null}
        </Grid>
      )}

      {activeTab === 1 && (
        <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            {banksLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                <CircularProgress size={48} />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Code</strong></TableCell>
                      <TableCell><strong>Cards</strong></TableCell>
                      <TableCell><strong>Offers</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {banks?.map((bank) => (
                      <TableRow
                        key={bank.id}
                        sx={{
                          '&:hover': {
                            bgcolor: 'rgba(102, 126, 234, 0.05)',
                          },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {bank.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={bank.code} size="small" sx={{ fontWeight: 600 }} />
                        </TableCell>
                        <TableCell>{bank.cards_count}</TableCell>
                        <TableCell>{bank.offers_count}</TableCell>
                        <TableCell>
                          <Chip
                            label={bank.is_active ? 'Active' : 'Inactive'}
                            color={bank.is_active ? 'success' : 'default'}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CreditCardIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                <Typography variant="h5" fontWeight={800}>
                  Credit Cards ({cards?.length || 0})
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => queryClient.invalidateQueries({ queryKey: ['adminCards'] })}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Refresh
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />
            {cardsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                <CircularProgress size={48} />
              </Box>
            ) : cards && cards.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Card Name</strong></TableCell>
                      <TableCell><strong>Bank</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Annual Fee</strong></TableCell>
                      <TableCell><strong>Users</strong></TableCell>
                      <TableCell><strong>Last Scraped</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cards.map((card) => (
                      <TableRow
                        key={card.id}
                        sx={{
                          '&:hover': {
                            bgcolor: 'rgba(102, 126, 234, 0.05)',
                          },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {card.name}
                          </Typography>
                        </TableCell>
                        <TableCell>{card.bank}</TableCell>
                        <TableCell>
                          <Chip
                            label={card.card_type}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(102, 126, 234, 0.1)',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            TT${card.annual_fee.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>{card.users_count}</TableCell>
                        <TableCell>
                          {card.last_scraped
                            ? new Date(card.last_scraped).toLocaleDateString()
                            : 'Never'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={card.is_active ? 'Active' : 'Inactive'}
                            color={card.is_active ? 'success' : 'default'}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No cards found. Use the Scraping tab to scrape cards from Trinidad & Tobago banks.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 3 && (
        <TTBanksView />
      )}

      {activeTab === 4 && (
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            border: '1px solid rgba(102, 126, 234, 0.1)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ScrapeIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={800} gutterBottom>
                    Trinidad & Tobago Bank Card Scraping
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Scrape credit card information from Trinidad & Tobago banks. This will fetch card details, rewards, and features from bank websites.
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 3 }} />
              <Button
                variant="contained"
                startIcon={<ScrapeIcon />}
                onClick={handleScrapeAll}
                disabled={Object.values(scrapingStatus).some(status => status === 'scraping')}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                    boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Scrape All T&T Banks
              </Button>
            </Box>

            <Grid container spacing={3}>
              {T_T_BANKS.map((bank) => {
                const status = scrapingStatus[bank.code] || 'idle';
                return (
                  <Grid item xs={12} sm={6} md={4} key={bank.code}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        border: '2px solid',
                        borderColor: status === 'scraping'
                          ? 'info.main'
                          : status === 'success'
                          ? 'success.main'
                          : status === 'error'
                          ? 'error.main'
                          : 'rgba(102, 126, 234, 0.2)',
                        background: status === 'success'
                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(34, 211, 153, 0.05) 100%)'
                          : status === 'error'
                          ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(248, 113, 113, 0.05) 100%)'
                          : 'white',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                          <Typography variant="h6" fontWeight={700}>
                            {bank.name}
                          </Typography>
                          <Chip
                            label={
                              status === 'scraping'
                                ? 'Scraping...'
                                : status === 'success'
                                ? 'Success'
                                : status === 'error'
                                ? 'Error'
                                : 'Ready'
                            }
                            color={
                              status === 'scraping'
                                ? 'info'
                                : status === 'success'
                                ? 'success'
                                : status === 'error'
                                ? 'error'
                                : 'default'
                            }
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </Stack>
                        <Button
                          fullWidth
                          variant={status === 'idle' ? 'contained' : 'outlined'}
                          onClick={() => handleScrape(bank.code)}
                          disabled={status === 'scraping'}
                          startIcon={status === 'scraping' ? <CircularProgress size={16} color="inherit" /> : <ScrapeIcon />}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            ...(status === 'idle' && {
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                              },
                            }),
                          }}
                        >
                          {status === 'scraping' ? 'Scraping...' : 'Scrape Cards'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      {activeTab === 4 && scrapingLogsData && (
        <>
          {/* Summary Statistics */}
          {scrapingLogsData.logs.length > 0 && (
            <Card
              sx={{
                mb: 3,
                borderRadius: 4,
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid rgba(102, 126, 234, 0.1)',
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight={800} gutterBottom>
                  Scraping Summary
                </Typography>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Total Scraping Operations
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color="primary">
                        {scrapingLogsData.logs.length}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Total Cards Found
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color="success.main">
                        {scrapingLogsData.logs.reduce((sum, log) => sum + log.offers_found, 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Cards Created
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color="info.main">
                        {scrapingLogsData.logs.reduce((sum, log) => sum + log.offers_created, 0)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Cards Updated
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color="warning.main">
                        {scrapingLogsData.logs.reduce((sum, log) => sum + log.offers_updated, 0)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          <Card
            sx={{
              borderRadius: 4,
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              border: '1px solid rgba(102, 126, 234, 0.1)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h5" fontWeight={800} gutterBottom>
                    Scraping Logs
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View all scraping operations and their scraped data in JSON format. Click "View JSON" to see the complete scraped card data.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['scrapingLogs'] })}
                  sx={{ textTransform: 'none' }}
                >
                  Refresh
                </Button>
              </Box>

            {scrapingLogsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : scrapingLogsData.logs.length === 0 ? (
              <Alert severity="info">No scraping logs found</Alert>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>ID</strong></TableCell>
                      <TableCell><strong>Bank</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Cards Found</strong></TableCell>
                      <TableCell><strong>Created</strong></TableCell>
                      <TableCell><strong>Updated</strong></TableCell>
                      <TableCell><strong>Started At</strong></TableCell>
                      <TableCell><strong>Duration</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scrapingLogsData.logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>{log.id}</TableCell>
                        <TableCell>
                          {log.bank ? `${log.bank.name} (${log.bank.code})` : 'All Banks'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.status}
                            color={
                              log.status === 'COMPLETED'
                                ? 'success'
                                : log.status === 'FAILED'
                                ? 'error'
                                : log.status === 'RUNNING'
                                ? 'info'
                                : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{log.offers_found}</TableCell>
                        <TableCell>{log.offers_created}</TableCell>
                        <TableCell>{log.offers_updated}</TableCell>
                        <TableCell>
                          {new Date(log.started_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {log.duration_seconds ? `${log.duration_seconds}s` : '-'}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setSelectedLogId(selectedLogId === log.id ? null : log.id)}
                          >
                            {selectedLogId === log.id ? 'Hide JSON' : 'View JSON'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* JSON Data Dialog */}
            {selectedLogId && scrapingLogsData.logs.find(log => log.id === selectedLogId) && (
              <Dialog
                open={true}
                onClose={() => setSelectedLogId(null)}
                maxWidth="lg"
                fullWidth
              >
                <DialogTitle>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        Scraped Data (JSON) - Log #{selectedLogId}
                      </Typography>
                      {(() => {
                        const log = scrapingLogsData.logs.find(log => log.id === selectedLogId);
                        if (log) {
                          return (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {log.bank ? `${log.bank.name} - ` : ''}
                              {log.offers_found} cards scraped | 
                              Created: {log.offers_created} | 
                              Updated: {log.offers_updated} | 
                              {log.completed_at ? new Date(log.completed_at).toLocaleString() : 'In Progress'}
                            </Typography>
                          );
                        }
                        return null;
                      })()}
                    </Box>
                    <IconButton onClick={() => setSelectedLogId(null)}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </DialogTitle>
                <DialogContent>
                  {(() => {
                    const log = scrapingLogsData.logs.find(log => log.id === selectedLogId);
                    if (!log || !log.scraped_data) {
                      return <Alert severity="info">No scraped data available for this log</Alert>;
                    }
                    
                    // Handle both old format (array) and new format (object with cards and metadata)
                    let dataArray: any[] = [];
                    let metadata: any = null;
                    
                    if (Array.isArray(log.scraped_data)) {
                      // Old format: direct array
                      dataArray = log.scraped_data;
                    } else if (log.scraped_data && typeof log.scraped_data === 'object') {
                      // New format: object with cards and metadata
                      if (log.scraped_data.cards && Array.isArray(log.scraped_data.cards)) {
                        dataArray = log.scraped_data.cards;
                        metadata = log.scraped_data.metadata || null;
                      } else {
                        // Fallback: try to convert to array
                        dataArray = [log.scraped_data];
                      }
                    } else {
                      dataArray = [];
                    }
                    
                    return (
                      <Box>
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Showing {dataArray.length} scraped {dataArray.length === 1 ? 'card' : 'cards'}
                            {metadata && (
                              <Chip 
                                label={`${metadata.cards_with_cashback || 0} with cashback`} 
                                size="small" 
                                color="success" 
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const jsonStr = JSON.stringify(log.scraped_data, null, 2);
                              navigator.clipboard.writeText(jsonStr);
                              setSnackbar({ open: true, message: 'JSON data copied to clipboard!', severity: 'success' });
                            }}
                          >
                            Copy JSON
                          </Button>
                        </Box>
                        {metadata && (
                          <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                              Scraping Metadata:
                            </Typography>
                            <Typography variant="body2">
                              Total Cards: {metadata.total_cards || 0}
                            </Typography>
                            <Typography variant="body2">
                              Cards with Cashback: {metadata.cards_with_cashback || 0}
                            </Typography>
                            {metadata.cards_with_reward_points !== undefined && (
                              <Typography variant="body2">
                                Cards with Reward Points: {metadata.cards_with_reward_points || 0}
                              </Typography>
                            )}
                            <Typography variant="body2">
                              Cashback Source: {metadata.cashback_source || 'N/A'}
                            </Typography>
                          </Box>
                        )}
                        <Box
                          sx={{
                            backgroundColor: '#1e1e1e',
                            borderRadius: 2,
                            p: 2,
                            maxHeight: '70vh',
                            overflow: 'auto',
                            '& pre': {
                              color: '#d4d4d4',
                              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                              fontSize: '0.875rem',
                              lineHeight: 1.6,
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            },
                          }}
                        >
                          <pre>{JSON.stringify(log.scraped_data, null, 2)}</pre>
                        </Box>
                        {dataArray.length > 0 && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                              Card Summary:
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell><strong>Card Name</strong></TableCell>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell><strong>Annual Fee</strong></TableCell>
                                    <TableCell><strong>Cashback Rate</strong></TableCell>
                                    <TableCell><strong>Rewards Rate</strong></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {dataArray.slice(0, 10).map((card: any, index: number) => (
                                    <TableRow key={index}>
                                      <TableCell>{card.name || 'N/A'}</TableCell>
                                      <TableCell>{card.card_type || 'N/A'}</TableCell>
                                      <TableCell>${card.annual_fee || 0}</TableCell>
                                      <TableCell>{card.cashback_rate || 0}%</TableCell>
                                      <TableCell>{card.reward_points_rate || 0}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            {dataArray.length > 10 && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                ... and {dataArray.length - 10} more cards (view full JSON above)
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setSelectedLogId(null)}>Close</Button>
                </DialogActions>
              </Dialog>
            )}
          </CardContent>
        </Card>
        </>
      )}

      {activeTab === 5 && (
        <Stack spacing={3}>
          {/* Categories */}
          <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)', border: '1px solid rgba(102, 126, 234, 0.1)' }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <MailIcon />
                  <Typography variant="h5" fontWeight={800}>
                    Gmail Expense Categories
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => seedCategoriesMutation.mutate()}
                  disabled={seedCategoriesMutation.isPending}
                >
                  Seed Defaults
                </Button>
              </Stack>

              {categoriesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : expenseCategories.length === 0 ? (
                <Alert severity="info">
                  No category configuration found yet. Click <strong>Seed Defaults</strong> to create editable category rows.
                </Alert>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Display Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {expenseCategories.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{c.code}</TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              value={c.display_name}
                              onChange={(e) =>
                                updateCategoryMutation.mutate({ id: c.id, data: { display_name: e.target.value } })
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={c.is_active}
                              onChange={(e) => updateCategoryMutation.mutate({ id: c.id, data: { is_active: e.target.checked } })}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={c.sort_order}
                              onChange={(e) =>
                                updateCategoryMutation.mutate({ id: c.id, data: { sort_order: Number(e.target.value) } })
                              }
                              sx={{ width: 120 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip label="Auto-saved" size="small" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Merchant patterns */}
          <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)', border: '1px solid rgba(102, 126, 234, 0.1)' }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight={800}>
                  Merchant Patterns (Rules)
                </Typography>
                <Button variant="contained" onClick={openNewPatternDialog}>
                  Add Rule
                </Button>
              </Stack>

              {patternsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : merchantPatterns.length === 0 ? (
                <Alert severity="info">No merchant patterns yet. Add rules to auto-map merchants and categories.</Alert>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Match</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {merchantPatterns.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{p.name}</TableCell>
                          <TableCell>
                            <Chip label={p.is_active ? 'Yes' : 'No'} color={p.is_active ? 'success' : 'default'} size="small" />
                          </TableCell>
                          <TableCell>{p.priority}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{p.match_type}: {p.match_value}</TableCell>
                          <TableCell>
                            <Chip label={p.category} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <Button size="small" onClick={() => openEditPatternDialog(p)}>Edit</Button>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => deletePatternMutation.mutate(p.id)}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Classification review */}
          <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)', border: '1px solid rgba(102, 126, 234, 0.1)' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" fontWeight={800} gutterBottom>
                Classification Review (Accuracy)
              </Typography>
              {reviewsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Add reviews by submitting a transaction ID and whether the category was correct. (This is the admin accuracy log.)
                </Alert>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="Transaction ID"
                  size="small"
                  type="number"
                  value={reviewTxnId}
                  onChange={(e) => setReviewTxnId(e.target.value ? Number(e.target.value) : '')}
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (!reviewTxnId) return;
                    createReviewMutation.mutate({ email_transaction: Number(reviewTxnId), is_correct: true });
                  }}
                >
                  Mark Correct
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => {
                    if (!reviewTxnId) return;
                    createReviewMutation.mutate({
                      email_transaction: Number(reviewTxnId),
                      is_correct: false,
                      corrected_category: 'OTHER',
                    });
                  }}
                >
                  Mark Incorrect (OTHER)
                </Button>
              </Stack>

              {classificationReviews.length > 0 && (
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Txn</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Correct</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Corrected Category</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {classificationReviews.slice(0, 20).map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>{r.email_transaction}</TableCell>
                          <TableCell>
                            <Chip label={r.is_correct ? 'Yes' : 'No'} color={r.is_correct ? 'success' : 'warning'} size="small" />
                          </TableCell>
                          <TableCell>{r.corrected_category || '-'}</TableCell>
                          <TableCell>{new Date(r.created_at).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* System health */}
          <Card sx={{ borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)', border: '1px solid rgba(102, 126, 234, 0.1)' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" fontWeight={800} gutterBottom>
                System Health (Email Ingestion Logs)
              </Typography>
              {logsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : ingestionLogs.length === 0 ? (
                <Alert severity="info">No ingestion logs yet.</Alert>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Start</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Processed</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Failed</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ingestionLogs.slice(0, 20).map((l) => (
                        <TableRow key={l.id}>
                          <TableCell>
                            <Chip label={l.status} size="small" color={l.status === 'SUCCESS' ? 'success' : l.status === 'FAILED' ? 'error' : 'default'} />
                          </TableCell>
                          <TableCell>{new Date(l.sync_start).toLocaleString()}</TableCell>
                          <TableCell>{l.emails_processed}</TableCell>
                          <TableCell>{l.emails_failed}</TableCell>
                          <TableCell>{l.error_message || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <Dialog open={patternDialogOpen} onClose={() => setPatternDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>{editingPattern ? 'Edit Merchant Pattern' : 'Add Merchant Pattern'}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Name"
                  value={patternForm.name || ''}
                  onChange={(e) => setPatternForm((p) => ({ ...p, name: e.target.value }))}
                  fullWidth
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!patternForm.is_active}
                      onChange={(e) => setPatternForm((p) => ({ ...p, is_active: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
                <TextField
                  label="Priority (lower = higher)"
                  type="number"
                  value={patternForm.priority ?? 100}
                  onChange={(e) => setPatternForm((p) => ({ ...p, priority: Number(e.target.value) }))}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Match Type</InputLabel>
                  <Select
                    value={patternForm.match_type || 'CONTAINS'}
                    label="Match Type"
                    onChange={(e) => setPatternForm((p) => ({ ...p, match_type: e.target.value as any }))}
                  >
                    <MenuItem value="CONTAINS">Contains</MenuItem>
                    <MenuItem value="STARTS_WITH">Starts With</MenuItem>
                    <MenuItem value="ENDS_WITH">Ends With</MenuItem>
                    <MenuItem value="REGEX">Regex</MenuItem>
                    <MenuItem value="FROM_EMAIL">From Email</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Match Value"
                  value={patternForm.match_value || ''}
                  onChange={(e) => setPatternForm((p) => ({ ...p, match_value: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Canonical Merchant (optional)"
                  value={patternForm.merchant_canonical || ''}
                  onChange={(e) => setPatternForm((p) => ({ ...p, merchant_canonical: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Category Code"
                  value={patternForm.category || 'OTHER'}
                  onChange={(e) => setPatternForm((p) => ({ ...p, category: e.target.value }))}
                  fullWidth
                  helperText="Example: UTILITIES, GROCERIES, RENT, OTHER..."
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPatternDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={savePattern}>
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Container>
    </Box>
  );
};

// T&T Banks View Component
const TTBanksView: React.FC = () => {
  const [selectedCard, setSelectedCard] = useState<TTBankWithCards['cards'][0] | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  
  const { data: banks, isLoading } = useQuery<TTBankWithCards[]>({
    queryKey: ['ttBanksWithCards'],
    queryFn: adminService.getTTBanksWithCards,
  });
  
  const handleShowDetails = (card: TTBankWithCards['cards'][0]) => {
    setSelectedCard(card);
    setDetailsDialogOpen(true);
  };
  
  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedCard(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!banks || banks.length === 0) {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <Alert severity="info">
            <Typography variant="h6" gutterBottom>
              No T&T Banks Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use the Scraping tab to scrape banks and cards from Trinidad & Tobago.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Trinidad & Tobago Banks
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Complete view of all T&T banks with their credit cards, images, and details
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {banks.map((bank) => (
          <Grid item xs={12} key={bank.id}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                  transform: 'translateY(-4px)',
                },
              }}
            >
              {/* Bank Header */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                  p: 4,
                  color: 'white',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                  {bank.logo_url ? (
                    <Box
                      component="img"
                      src={bank.logo_url}
                      alt={bank.name}
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: 'contain',
                        bgcolor: 'rgba(255,255,255,0.1)',
                        borderRadius: 2,
                        p: 1,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AccountBalance sx={{ fontSize: 48 }} />
                    </Box>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" fontWeight={800} gutterBottom>
                      {bank.name}
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                      <Chip
                        icon={<FlagIcon />}
                        label={bank.code}
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                        size="small"
                      />
                      <Chip
                        label={bank.bank_type}
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                        size="small"
                      />
                      {bank.operating_category && (
                        <Chip
                          label={operatingCategoryLabel(bank.operating_category)}
                          sx={{
                            bgcolor:
                              bank.operating_category === 'TRADITIONAL'
                                ? 'rgba(16, 185, 129, 0.28)'
                                : bank.operating_category === 'NON_TRADITIONAL'
                                ? 'rgba(245, 158, 11, 0.28)'
                                : 'rgba(148, 163, 184, 0.28)',
                            color: 'white',
                            fontWeight: 700,
                          }}
                          size="small"
                        />
                      )}
                      <Chip
                        icon={<CreditCardIcon />}
                        label={`${bank.active_cards} Cards`}
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                        size="small"
                      />
                    </Stack>
                  </Box>
                </Box>

                {/* Bank Details */}
                <Grid container spacing={2}>
                  {bank.website && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LanguageIcon sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Typography
                          variant="body2"
                          component="a"
                          href={bank.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: 'white',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {bank.website}
                          <OpenInNewIcon sx={{ fontSize: 16 }} />
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {bank.support_email && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {bank.support_email}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {bank.support_phone && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {bank.support_phone}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {bank.headquarters && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon sx={{ fontSize: 20, opacity: 0.9 }} />
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {bank.headquarters}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {bank.established_year && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Established: {bank.established_year}
                      </Typography>
                    </Grid>
                  )}
                  {bank.total_branches && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {bank.total_branches} Branches
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Cards Section */}
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
                  Credit Cards ({bank.cards.length})
                </Typography>

                {bank.cards.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      No cards available for this bank. Use the Scraping tab to scrape cards.
                    </Typography>
                  </Alert>
                ) : (
                  <Grid container spacing={3}>
                    {bank.cards.map((card) => (
                      <Grid item xs={12} sm={6} md={4} key={card.id}>
                        <Card
                          sx={{
                            height: '100%',
                            borderRadius: 3,
                            border: '1px solid #e5e7eb',
                            transition: 'all 0.3s ease',
                            overflow: 'hidden',
                            '&:hover': {
                              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                              transform: 'translateY(-4px)',
                            },
                          }}
                        >
                          {/* Card Image */}
                          {card.image_url ? (
                            <Box
                              component="img"
                              src={card.image_url}
                              alt={card.name}
                              sx={{
                                width: '100%',
                                height: 200,
                                objectFit: 'cover',
                                bgcolor: '#f8fafc',
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: '100%',
                                height: 200,
                                background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <CreditCardIcon sx={{ fontSize: 64, color: 'white', opacity: 0.5 }} />
                            </Box>
                          )}

                          <CardContent>
                            <Typography variant="h6" fontWeight={700} gutterBottom>
                              {card.name}
                            </Typography>
                            <Chip
                              label={card.card_type}
                              size="small"
                              sx={{ mb: 2, bgcolor: '#f1f5f9' }}
                            />

                            <Stack spacing={1.5}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                  Annual Fee:
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {card.reward_currency_basis === 'USD' ? 'US' : 'TT'}${Number(card.annual_fee).toFixed(2)}
                                </Typography>
                              </Box>

                              {Number(card.cashback_rate) > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Cashback:
                                  </Typography>
                                  <Chip
                                    label={`${Number(card.cashback_rate).toFixed(2)}%`}
                                    size="small"
                                    sx={{ bgcolor: '#10b981', color: 'white', fontWeight: 700 }}
                                  />
                                </Box>
                              )}

                              {Number(card.reward_points_rate) > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Points:
                                  </Typography>
                                  <Chip
                                    label={`${Number(card.reward_points_rate).toFixed(2)}x (${card.reward_currency_basis || 'TTD'})`}
                                    size="small"
                                    sx={{ bgcolor: '#6366f1', color: 'white', fontWeight: 700 }}
                                  />
                                </Box>
                              )}

                              {Number(card.cashback_rate) === 0 && Number(card.reward_points_rate) === 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Rewards:
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    No cashback / points
                                  </Typography>
                                </Box>
                              )}

                              {card.interest_rate && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Interest Rate:
                                  </Typography>
                                  <Typography variant="body2" fontWeight={600}>
                                    {Number(card.interest_rate).toFixed(2)}%
                                  </Typography>
                                </Box>
                              )}

                              {card.last_scraped && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Last Scraped:
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(card.last_scraped).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              )}

                              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                {card.scraping_url && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<OpenInNewIcon />}
                                    href={card.scraping_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    View on Bank Website
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  variant="contained"
                                  fullWidth
                                  startIcon={<InfoIcon />}
                                  onClick={() => handleShowDetails(card)}
                                  sx={{
                                    background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #5568d3 0%, #d9468f 100%)',
                                    },
                                  }}
                                >
                                  Show Details
                                </Button>
                              </Stack>
                            </Stack>

                            {card.features && (
                              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #f3f4f6' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  Features:
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                                  {card.features.substring(0, 150)}
                                  {card.features.length > 150 ? '...' : ''}
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {/* Card Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {selectedCard?.name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Complete Scraped Card Details
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDetails}
            sx={{ color: 'white' }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {selectedCard && (
            <Grid container spacing={3}>
              {/* Card Image */}
              {selectedCard.image_url && (
                <Grid item xs={12}>
                  <Box
                    component="img"
                    src={selectedCard.image_url}
                    alt={selectedCard.name}
                    sx={{
                      width: '100%',
                      maxHeight: 300,
                      objectFit: 'contain',
                      borderRadius: 2,
                      bgcolor: '#f8fafc',
                      p: 2,
                    }}
                  />
                </Grid>
              )}
              
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Card Type
                    </Typography>
                    <Chip label={selectedCard.card_type} size="small" sx={{ bgcolor: '#f1f5f9' }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Annual Fee
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {selectedCard.reward_currency_basis === 'USD' ? 'US' : 'TT'}${Number(selectedCard.annual_fee).toFixed(2)}
                    </Typography>
                  </Grid>
                  {selectedCard.interest_rate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Interest Rate
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {Number(selectedCard.interest_rate).toFixed(2)}%
                      </Typography>
                    </Grid>
                  )}
                  {selectedCard.credit_limit_min && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Credit Limit Range
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        TT${Number(selectedCard.credit_limit_min).toFixed(2)}
                        {selectedCard.credit_limit_max && ` - TT$${Number(selectedCard.credit_limit_max).toFixed(2)}`}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Rewards Information (always show scraped values) */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                  Rewards & Benefits (from scraping)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Cashback Rate
                    </Typography>
                    {Number(selectedCard.cashback_rate) > 0 ? (
                      <Chip
                        label={`${Number(selectedCard.cashback_rate).toFixed(2)}%`}
                        size="medium"
                        sx={{ bgcolor: '#10b981', color: 'white', fontWeight: 700 }}
                      />
                    ) : (
                      <Typography variant="body1" color="text.secondary">—</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Reward Points Rate
                    </Typography>
                    {Number(selectedCard.reward_points_rate) > 0 ? (
                      <Chip
                        label={`${Number(selectedCard.reward_points_rate).toFixed(2)}x per ${selectedCard.reward_currency_basis || 'TTD'} spent`}
                        size="medium"
                        sx={{ bgcolor: '#6366f1', color: 'white', fontWeight: 700 }}
                      />
                    ) : (
                      <Typography variant="body1" color="text.secondary">—</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Reward currency basis
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {selectedCard.reward_currency_basis || '—'}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              
              {/* Welcome Bonus */}
              {selectedCard.welcome_bonus && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                      Welcome Bonus
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedCard.welcome_bonus}
                    </Typography>
                  </Grid>
                </>
              )}
              
              {/* Features */}
              {selectedCard.features && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                      Features & Benefits
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedCard.features}
                    </Typography>
                  </Grid>
                </>
              )}
              
              {/* Requirements */}
              {selectedCard.requirements && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                      Requirements
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedCard.requirements}
                    </Typography>
                  </Grid>
                </>
              )}
              
              {/* Scraping Information */}
              <Divider sx={{ my: 2 }} />
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                  Scraping Information
                </Typography>
                <Grid container spacing={2}>
                  {selectedCard.scraping_url && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Source URL
                      </Typography>
                      <Typography
                        variant="body2"
                        component="a"
                        href={selectedCard.scraping_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        {selectedCard.scraping_url}
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </Typography>
                    </Grid>
                  )}
                  {selectedCard.last_scraped && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Last Scraped
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {new Date(selectedCard.last_scraped).toLocaleString()}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Status
                    </Typography>
                    <Chip
                      label={selectedCard.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        bgcolor: selectedCard.is_active ? '#10b981' : '#ef4444',
                        color: 'white',
                        fontWeight: 700,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Created At
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {new Date(selectedCard.created_at).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          {selectedCard?.scraping_url && (
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              href={selectedCard.scraping_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View on Bank Website
            </Button>
          )}
          <Button variant="contained" onClick={handleCloseDetails}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPanel;
